import { getEventMentionParts } from '../../../../utils/eventMentionParts';
import React from 'react';

const MAX_MENTION_LABEL_LENGTH = 80;
export const UNASSIGNED_PART_MENTION_ID = '__unassigned_part__';
export const UNASSIGNED_PART_MENTION_LABEL = 'パート未設定';

export const normalizeMentionLabel = (value = '') =>
  String(value || '')
    .replace(/^[@＠]+/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_MENTION_LABEL_LENGTH);

export const parseMentionPartIds = (value) => {
  if (!value) return [];
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.replace(/[{}[\]]/g, '').split(',')
      : [];
  return Array.from(new Set(
    source
      .map((entry) => Number.parseInt(entry, 10))
      .filter((entry) => Number.isInteger(entry) && entry > 0)
  ));
};

export const findMentionTrigger = (text = '', cursorIndex = 0) => {
  const source = String(text || '');
  const cursor = Math.max(0, Math.min(Number(cursorIndex) || 0, source.length));
  const beforeCursor = source.slice(0, cursor);
  const atIndex = beforeCursor.lastIndexOf('@');
  if (atIndex < 0) return null;
  if (atIndex > 0 && !/\s/.test(source.charAt(atIndex - 1))) return null;

  const query = source.slice(atIndex + 1, cursor);
  if (/[\s@＠]/.test(query)) return null;
  return { start: atIndex, end: cursor, query };
};

export const buildMentionInsertion = ({
  text = '',
  mentionState = null,
  selection = {},
  label = '',
} = {}) => {
  const normalizedLabel = normalizeMentionLabel(label);
  if (!normalizedLabel || !mentionState) return null;

  const source = String(text || '');
  const stateStart = Math.max(
    0,
    Math.min(Number.parseInt(mentionState.start, 10) || 0, source.length)
  );
  const stateEnd = Math.max(
    stateStart,
    Math.min(Number.parseInt(mentionState.end, 10) || stateStart, source.length)
  );
  const rawSelectionStart = Number.parseInt(selection?.start, 10);
  const rawSelectionEnd = Number.parseInt(selection?.end, 10);
  const selectionStart = Number.isInteger(rawSelectionStart) ? rawSelectionStart : stateEnd;
  const selectionEnd = Number.isInteger(rawSelectionEnd) ? rawSelectionEnd : selectionStart;
  const replaceEnd = Math.max(
    stateEnd,
    Math.min(Math.max(selectionStart, selectionEnd), source.length)
  );
  const trailingAtMatch = source.slice(replaceEnd).match(/^[@＠]+/);
  const afterStart = replaceEnd + (trailingAtMatch ? trailingAtMatch[0].length : 0);
  const before = source.slice(0, stateStart);
  const mentionText = `@${normalizedLabel}`;
  const insertion = `${mentionText} `;
  const nextText = `${before}${insertion}${source.slice(afterStart)}`;
  const mentionStart = before.length;
  const mentionEnd = mentionStart + mentionText.length;
  const nextCursor = mentionStart + insertion.length;

  return {
    text: nextText,
    mentionText,
    insertion,
    selection: { start: nextCursor, end: nextCursor },
    mention: {
      label: normalizedLabel,
      start: mentionStart,
      end: mentionEnd,
    },
  };
};

const pickFirstString = (...values) => {
  for (const value of values) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) return text;
  }
  return '';
};

const normalizeId = (value) => String(value || '').trim();

export const normalizeMentionTargetType = (mention = {}) =>
  String(mention?.type || mention?.targetType || mention?.target_type || '')
    .trim()
    .toLowerCase();

export const normalizeMentionTargetRole = (mention = {}) =>
  String(mention?.role || mention?.targetRole || mention?.target_role || '')
    .trim()
    .toLowerCase();

export const normalizeMentionTargetId = (mention = {}) =>
  normalizeId(mention?.targetId ?? mention?.target_id ?? mention?.id);

const stripKnownPrefix = (value, prefixes = []) => {
  const text = normalizeId(value);
  if (!text) return '';
  const matched = prefixes.find((prefix) => text.startsWith(prefix));
  return matched ? text.slice(matched.length) : text;
};

const getAdminCandidateId = (admin = {}) =>
  normalizeId(
    admin?.admin_user_id ??
      admin?.id ??
      admin?.adminUserId ??
      admin?.payload?.admin_user_id ??
      admin?.payload?.id ??
      admin?.payload?.adminUserId
  ) || stripKnownPrefix(admin?.key, ['admin-']);

const getArtistCandidateId = (artist = {}) =>
  normalizeId(
    artist?.artist_id ??
      artist?.id ??
      artist?.artist_user_id ??
      artist?.artistId ??
      artist?.payload?.artist_id ??
      artist?.payload?.id ??
      artist?.payload?.artist_user_id ??
      artist?.payload?.artistId
  ) || stripKnownPrefix(artist?.key, ['artist-', 'concierge-']);

const normalizeCurrentMentionUser = (currentUser = null) => {
  if (!currentUser || typeof currentUser !== 'object') return null;
  const role = normalizeId(
    currentUser?.role ??
      currentUser?.targetRole ??
      currentUser?.target_role ??
      currentUser?.userRole ??
      currentUser?.user_role
  ).toLowerCase();
  const id = normalizeId(
    currentUser?.id ??
      currentUser?.userId ??
      currentUser?.user_id ??
      currentUser?.adminUserId ??
      currentUser?.admin_user_id ??
      currentUser?.artistId ??
      currentUser?.artist_id ??
      currentUser?.artistUserId ??
      currentUser?.artist_user_id
  );
  if (!role || !id) return null;
  if (role !== 'admin' && role !== 'artist') return null;
  return { role, id };
};

const isCurrentMentionUser = (currentUser, role, id) =>
  Boolean(currentUser?.role && currentUser?.id) &&
  currentUser.role === role &&
  currentUser.id === normalizeId(id);

const getCandidatePartIds = (artist = {}) =>
  parseMentionPartIds(
    artist?.artist_parts ??
      artist?.parts ??
      artist?.part_ids ??
      artist?.partIds ??
      artist?.payload?.artist_parts ??
      artist?.payload?.parts ??
      artist?.payload?.part_ids ??
      artist?.payload?.partIds
  );

const getRawCandidatePartLabels = (artist = {}) =>
  [
    artist?.artist_part_short_name,
    artist?.part_short_name,
    artist?.main_part_short_name,
    artist?.artist_part_label,
    artist?.part_label,
    artist?.main_part_label,
    artist?.partLabel,
    artist?.badge,
    artist?.eventPartLabel,
    artist?.allPerformancesGroupLabel,
    artist?.payload?.artist_part_short_name,
    artist?.payload?.part_short_name,
    artist?.payload?.main_part_short_name,
    artist?.payload?.artist_part_label,
    artist?.payload?.part_label,
    artist?.payload?.main_part_label,
    artist?.payload?.partLabel,
    artist?.payload?.eventPartLabel,
  ]
    .map(normalizeMentionLabel)
    .filter(Boolean);

const normalizePartMap = (parts = []) => {
  const map = new Map();
  (Array.isArray(parts) ? parts : []).forEach((part) => {
    const id = Number.parseInt(part?.id ?? part?.part_id, 10);
    if (!Number.isInteger(id) || id <= 0) return;
    map.set(id, {
      id,
      label: pickFirstString(part?.label, part?.name, part?.part_name, part?.short_name),
      shortLabel: pickFirstString(part?.short_name, part?.abbr, part?.label, part?.name),
      sortOrder: Number.isFinite(Number(part?.sort_order)) ? Number(part.sort_order) : 9999,
    });
  });
  return map;
};

/** @param {{ admins?: object[], artists?: object[], parts?: object[], includeAdmins?: boolean, includeArtists?: boolean, includeParts?: boolean, scope?: string, currentUser?: object | null }} options */
export const buildChatMentionCandidates = ({
  admins = [],
  artists = [],
  parts = [],
  includeAdmins = true,
  includeArtists = true,
  includeParts = true,
  scope = 'group',
  currentUser = null,
} = {}) => {
  const candidates = [];
  const seenPersons = new Set();
  const partsById = normalizePartMap(parts);
  const normalizedCurrentUser = normalizeCurrentMentionUser(currentUser);

  if (includeAdmins) {
    (Array.isArray(admins) ? admins : []).forEach((admin) => {
      const id = getAdminCandidateId(admin);
      if (isCurrentMentionUser(normalizedCurrentUser, 'admin', id)) return;
      if (!id || seenPersons.has(`admin:${id}`)) return;
      seenPersons.add(`admin:${id}`);
      const label = normalizeMentionLabel(
        pickFirstString(
          admin?.display_name,
          admin?.full_name,
          admin?.name,
          admin?.username,
          admin?.payload?.display_name,
          admin?.payload?.full_name,
          admin?.payload?.name,
          admin?.payload?.username
        )
      );
      if (!label) return;
      const profilePicture = pickFirstString(
        admin?.profile_picture,
        admin?.profilePicture,
        admin?.payload?.profile_picture,
        admin?.payload?.profilePicture
      );
      candidates.push({
        type: 'person',
        role: 'admin',
        id,
        label,
        subtitle: pickFirstString(
          admin?.position,
          admin?.department,
          admin?.role,
          admin?.payload?.position,
          admin?.payload?.department,
          admin?.payload?.role,
          '運営スタッフ'
        ),
        avatarUrl: admin?.avatarUrl || '',
        profilePicture,
        workAvailability: admin?.work_availability || admin?.workAvailability || null,
      });
    });
  }

  const eventPartCounts = new Map();
  const partCounts = new Map();
  const partLabelCounts = new Map();
  let unassignedPartCount = 0;
  if (includeArtists) {
    (Array.isArray(artists) ? artists : []).forEach((artist) => {
      const id = getArtistCandidateId(artist);
      if (artist?.has_chat_access === false || artist?.payload?.has_chat_access === false) return;
      if (isCurrentMentionUser(normalizedCurrentUser, 'artist', id)) return;
      if (!id || seenPersons.has(`artist:${id}`)) return;
      seenPersons.add(`artist:${id}`);
      if (scope === 'event') {
        getEventMentionParts(artist).forEach((part) => {
          const previous = eventPartCounts.get(part.id);
          eventPartCounts.set(part.id, { ...part, count: (previous?.count || 0) + 1 });
        });
      }
      const rawPartLabels = getRawCandidatePartLabels(artist);
      const partIds = getCandidatePartIds(artist);
      const partLabels = [...new Set(rawPartLabels.filter((label) => label !== UNASSIGNED_PART_MENTION_LABEL))];
      partIds.forEach((partId) => partCounts.set(partId, (partCounts.get(partId) || 0) + 1));
      if (!partIds.length) {
        partLabels.forEach((label) => partLabelCounts.set(label, (partLabelCounts.get(label) || 0) + 1));
      }
      const isUnassignedPart =
        (!partIds.length && !partLabels.length) ||
        rawPartLabels.includes(UNASSIGNED_PART_MENTION_LABEL);
      if (isUnassignedPart) unassignedPartCount += 1;
      const label = normalizeMentionLabel(
        pickFirstString(
          artist?.artist_name,
          artist?.name,
          artist?.name_en,
          artist?.payload?.artist_name,
          artist?.payload?.name,
          artist?.payload?.name_en
        )
      );
      if (!label) return;
      const profilePicture = pickFirstString(
        artist?.profile_picture,
        artist?.profilePicture,
        artist?.artist_profile_picture,
        artist?.payload?.profile_picture,
        artist?.payload?.profilePicture,
        artist?.payload?.artist_profile_picture
      );
      const primaryPartId = partIds[0];
      const partMeta = partsById.get(primaryPartId);
      const subtitle = pickFirstString(
        partLabels[0],
        artist?.artist_part_short_name,
        artist?.part_short_name,
        artist?.artist_part_label,
        artist?.part_label,
        partMeta?.shortLabel,
        partMeta?.label,
        UNASSIGNED_PART_MENTION_LABEL
      );
      candidates.push({
        type: 'person',
        role: 'artist',
        id,
        label,
        subtitle: scope === 'event' ? getEventMentionParts(artist).map((part) => part.label).join(' / ') : subtitle,
        avatarUrl: artist?.avatarUrl || artist?.payload?.avatarUrl || '',
        profilePicture,
      });
    });
  }

  if (includeParts && scope === 'event') {
    [...eventPartCounts.values()]
      .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label, 'ja'))
      .forEach((part) => candidates.push({ type: 'part', role: 'artist', id: part.id, label: part.label, subtitle: `${part.count}名`, sortOrder: part.sortOrder }));
  }

  if (includeParts && scope !== 'event') {
    const emittedPartLabels = new Set();
    Array.from(partCounts.entries())
      .map(([partId, count]) => {
        const partMeta = partsById.get(partId);
        const label = normalizeMentionLabel(partMeta?.shortLabel || partMeta?.label || `Part ${partId}`);
        if (!label) return null;
        return {
          type: 'part',
          role: 'artist',
          id: String(partId),
          label,
          subtitle: `${count}名`,
          sortOrder: partMeta?.sortOrder ?? 9999,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.sortOrder - b.sortOrder) || a.label.localeCompare(b.label, 'ja'))
      .forEach((candidate) => {
        emittedPartLabels.add(candidate.label);
        candidates.push(candidate);
      });

    Array.from(partLabelCounts.entries())
      .filter(([label]) => label && !emittedPartLabels.has(label))
      .sort(([a], [b]) => a.localeCompare(b, 'ja'))
      .forEach(([label, count]) => {
        candidates.push({
          type: 'part',
          role: 'artist',
          id: label,
          label,
          subtitle: `${count}名`,
          sortOrder: 9999,
        });
      });

    if (unassignedPartCount > 0) {
      candidates.push({
        type: 'part',
        role: 'artist',
        id: UNASSIGNED_PART_MENTION_ID,
        label: UNASSIGNED_PART_MENTION_LABEL,
        subtitle: `${unassignedPartCount}名`,
        sortOrder: 10000,
      });
    }
  }

  return candidates;
};

export const buildOutgoingMentions = (selectedMentions = [], content = '') => {
  const text = String(content || '');
  if (!text.trim() || !Array.isArray(selectedMentions)) return [];
  const out = [];
  const seen = new Set();

  selectedMentions.forEach((mention) => {
    const label = normalizeMentionLabel(mention?.label);
    const id = normalizeId(mention?.id ?? mention?.targetId);
    const type = mention?.type === 'part' ? 'part' : 'person';
    const role = type === 'part' ? 'artist' : String(mention?.role || mention?.targetRole || '').trim();
    if (!label || !id || (type === 'person' && !role)) return;
    const mentionText = `@${label}`;
    let start = text.indexOf(mentionText);
    while (start >= 0) {
      const end = start + mentionText.length;
      const leftOk = start === 0 || /\s/.test(text.charAt(start - 1));
      const rightOk = end >= text.length || /\s/.test(text.charAt(end));
      if (leftOk && rightOk) break;
      start = text.indexOf(mentionText, start + 1);
    }
    if (start < 0) return;
    const end = start + mentionText.length;
    const key = `${type}:${role}:${id}:${start}:${end}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      type,
      role,
      targetId: id,
      label,
      start,
      end,
    });
  });

  return out;
};

export const renderMentionTextNodes = ({
  text = '',
  mentions = [],
  linkifyText = null,
  onMentionClick = null,
} = {}) => {
  const source = String(text || '');
  if (!source) return '';
  const normalized = (Array.isArray(mentions) ? mentions : [])
    .map((mention) => {
      const label = normalizeMentionLabel(mention?.label || mention?.displayLabel || mention?.display_label);
      const start = Number.parseInt(mention?.start ?? mention?.start_index, 10);
      const end = Number.parseInt(mention?.end ?? mention?.end_index, 10);
      if (!label || !Number.isInteger(start) || !Number.isInteger(end) || end <= start) return null;
      if (source.slice(start, end) !== `@${label}`) return null;
      return { start, end, label, mention };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);

  if (!normalized.length) {
    return typeof linkifyText === 'function' ? linkifyText(source) : source;
  }

  const nodes = [];
  let cursor = 0;
  normalized.forEach((mention, index) => {
    if (mention.start < cursor) return;
    if (cursor < mention.start) {
      const segment = source.slice(cursor, mention.start);
      nodes.push(
        <React.Fragment key={`text-${cursor}`}>
          {typeof linkifyText === 'function' ? linkifyText(segment) : segment}
        </React.Fragment>
      );
    }
    const token = source.slice(mention.start, mention.end);
    const mentionPayload = {
      ...mention.mention,
      label: mention.label,
      displayLabel: mention.label,
      start: mention.start,
      end: mention.end,
    };
    nodes.push(
      typeof onMentionClick === 'function' ? (
        <button data-selection-safe-navigation
          type="button"
          key={`mention-${mention.start}-${index}`}
          className="chat-mention-token chat-mention-token--button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onMentionClick(mentionPayload);
          }}
        >
          {token}
        </button>
      ) : (
        <span key={`mention-${mention.start}-${index}`} className="chat-mention-token">
          {token}
        </span>
      )
    );
    cursor = mention.end;
  });

  if (cursor < source.length) {
    const segment = source.slice(cursor);
    nodes.push(
      <React.Fragment key={`text-${cursor}`}>
        {typeof linkifyText === 'function' ? linkifyText(segment) : segment}
      </React.Fragment>
    );
  }

  return nodes;
};
